import {
  deriveOnboardingStatusValue,
  type OnboardingStatus,
  type OnboardingStatusValue,
} from "./onboarding.api";
import { useOrgOnboardingStatusQuery } from "./useOrgOnboardingStatusQuery";

import { useAuth } from "@/state/AuthContext";
import { isPlatformAdmin, isPlatformMember, isPlatformViewer } from "@/utils/access";

export type OrgHomeState = {
  isLoading: boolean;
  /** True when the status fetch failed (after retries). Does not invent onboarding state. */
  isError: boolean;
  status: OnboardingStatus | undefined;
  /** `undefined` while loading or after a failed fetch — never a false `"not_started"`. */
  onboardingStatus: OnboardingStatusValue | undefined;
  workspaceCount: number;
  mustOnboard: boolean;
  skipped: boolean;
  isAdmin: boolean;
  isMember: boolean;
  isViewer: boolean;
};

export function useOrgHomeState(): OrgHomeState {
  const { user } = useAuth();

  const q = useOrgOnboardingStatusQuery();

  const status = q.data;
  const workspaceCount = Number(status?.workspaceCount ?? 0);
  const mustOnboard = Boolean(status?.mustOnboard ?? false);
  const skipped = Boolean(status?.skipped ?? false);
  // Only derive from confirmed payload. Missing/error → undefined (fail open for redirect).
  const onboardingStatus = q.isError && !status ? undefined : deriveOnboardingStatusValue(status);

  const isAdmin = isPlatformAdmin(user);
  const isMember = isPlatformMember(user);
  const isViewer = isPlatformViewer(user);

  return {
    /** True until first fetch settles (success or error). Legacy pages may show a skeleton. */
    isLoading: q.isPending && !q.isError,
    isError: q.isError && !status,
    status,
    onboardingStatus,
    workspaceCount,
    mustOnboard,
    skipped,
    isAdmin,
    isMember,
    isViewer,
  };
}
